import React, { useState, useRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { Typography, Grid } from "@mui/material";
import { TextField } from "@totalsoft/rocket-ui";
import PersonIcon from "@mui/icons-material/Person";
import { useDrag, useDrop } from "react-dnd";
import { get, set } from "@totalsoft/change-tracking-react";
import ListActionsMenu from "features/common/components/list-actions-menu/ListActionsMenu";
import { partnerAssociationModalStateVar } from "./partnerAssociationModal/variables";
import { newChildForNode } from "../functions";

const stopPropagation = e => e.stopPropagation();

const HierarchyDetailsTreeItemComponent = ({
  saving,
  itemId,
  label,
  item,
  handleSave,
  handleDelete,
  canWrite,
  children,
  hierarchyName
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const history = useHistory();
  const [t] = useTranslation();
  const ref = useRef(null);

  const [{ isOverCurrent }, drop] = useDrop({
    accept: "TreeItem",
    collect: monitor => ({
      isOver: monitor.isOver(),
      isOverCurrent: monitor.isOver({ shallow: true })
    })
  });

  const [{ isDragging }, drag] = useDrag(() => ({
    type: "TreeItem",
    item: { id: itemId, label },
    collect: monitor => {
      return {
        isDragging: monitor.isDragging()
      };
    }
  }));

  drag(drop(ref));

  const handleDoubleClick = useCallback(() => {
    if (!canWrite) return;
    if (item.data.partnerId |> get) {
      setShowMessage(true);
    } else {
      setIsEditing(true);
    }
  }, [canWrite, item.data.partnerId]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    handleSave(item.data |> get);
  }, [handleSave, item.data]);

  const handleAdd = useCallback(() => {
    handleSave(newChildForNode(item |> get));
  }, [handleSave, item]);

  const handleRemove = useCallback(
    () =>
      handleDelete(
        item.data.partnerHierarchyDetailId |> get,
        item.data.partnerHierarchyId |> get,
        item.data.path |> get
      ),
    [handleDelete, item.data.partnerHierarchyDetailId, item.data.partnerHierarchyId, item.data.path]
  );

  const handleRedirect = useCallback(
    e => {
      stopPropagation(e);
      history.push(`/partners/${item.data.partnerId |> get}`);
    },
    [history, item.data.partnerId]
  );

  const openPartnerAssociationModal = useCallback(
    isMultipleSelection => {
      partnerAssociationModalStateVar({
        open: true,
        data: {
          nodeId: item.data.partnerHierarchyDetailId |> get,
          nodeCategory: item.data.category |> get,
          nodeHierarchy: hierarchyName,
          singleSelection: isMultipleSelection
        }
      });
    },
    [item.data, hierarchyName]
  );

  const handleAssociatePartner = useCallback(() => {
    openPartnerAssociationModal(true);
  }, [openPartnerAssociationModal]);

  const handleAssociatePartners = useCallback(() => {
    openPartnerAssociationModal(false);
  }, [openPartnerAssociationModal]);

  const handleDissociatePartner = useCallback(() => {
    handleSave({ ...(item.data |> get), partnerId: null });
  }, [handleSave, item.data]);

  const handleRemoveErrorMessage = useCallback(() => {
    setShowMessage(false);
  }, []);

  const actions = useMemo(
    () =>
      item.data.partnerId |> get
        ? [
            { caption: "PartnerHierarchy.AddNode", handler: handleAdd, icon: "add", color: "secondary" },
            { caption: "PartnerHierarchy.DeleteNode", handler: handleRemove, icon: "delete", color: "error" },
            {
              caption: "PartnerHierarchy.ChangeAssociatePartner",
              handler: handleAssociatePartner,
              icon: "person_add",
              color: "secondary"
            },
            {
              caption: "PartnerHierarchy.AssociatePartners",
              handler: handleAssociatePartners,
              icon: "group_add",
              color: "secondary"
            },
            {
              caption: "PartnerHierarchy.DissociatePartner",
              handler: handleDissociatePartner,
              icon: "person_remove",
              color: "error"
            }
          ]
        : [
            { caption: "PartnerHierarchy.AddNode", handler: handleAdd, icon: "add", color: "secondary" },
            { caption: "PartnerHierarchy.DeleteNode", handler: handleRemove, icon: "delete", color: "error" },
            {
              caption: "PartnerHierarchy.AssociatePartner",
              handler: handleAssociatePartner,
              icon: "person_add",
              color: "secondary"
            },
            {
              caption: "PartnerHierarchy.AssociatePartners",
              handler: handleAssociatePartners,
              icon: "group_add",
              color: "secondary"
            }
          ],
    [
      handleAdd,
      handleAssociatePartner,
      handleAssociatePartners,
      handleDissociatePartner,
      handleRemove,
      item.data.partnerId
    ]
  );

  return (
    <TreeItem
      itemId={itemId}
      ref={ref}
      sx={{ opacity: isDragging ? 0.5 : 1, backgroundColor: isOverCurrent ? "lightBlue" : "inherit" }}
      onMouseOut={handleRemoveErrorMessage}
      label={
        <Grid container spacing={2} display="flex" alignItems="center">
          {isEditing ? (
            <Grid item xs={2}>
              <TextField
                value={item.data.category |> get}
                onChange={item.data.category |> set}
                onBlur={handleBlur}
                onKeyDown={stopPropagation}
                autoFocus
                size="small"
              />
            </Grid>
          ) : (
            <Grid item xs={2}>
              <Typography onDoubleClick={handleDoubleClick}>
                {(item.data.partnerName |> get) ?? (item.data.category |> get)}
              </Typography>
            </Grid>
          )}

          <Grid item xs={9}>
            {showMessage && (
              <Typography color="error" sx={{ ml: 2 }}>
                {t("PartnerHierarchy.NodeCannotBeEdited")}
              </Typography>
            )}
          </Grid>
          <Grid item xs={0.5}>
            {Boolean(item.data.partnerId |> get) && (
              <PersonIcon onClick={handleRedirect} sx={{ ml: 2, "&:hover": { color: "blue" } }} />
            )}
          </Grid>
          <Grid item xs={0.5}>
            {canWrite && <ListActionsMenu actions={actions} disabled={!canWrite || saving} />}
          </Grid>
        </Grid>
      }
    >
      {children}
    </TreeItem>
  );
};

HierarchyDetailsTreeItemComponent.propTypes = {
  saving: PropTypes.bool.isRequired,
  itemId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  item: PropTypes.object,
  children: PropTypes.node,
  handleSave: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  canWrite: PropTypes.bool,
  hierarchyName: PropTypes.string
};

export default HierarchyDetailsTreeItemComponent;
